

import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useUser } from '@/contexts/UserContext'
import { setReturnUrl, popReturnUrl, completeOAuthReturn } from '@/lib/api'
import { Input } from '@/components/ui/shadcn/input'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/shadcn/tabs'
import Button from '@/components/ui/Button'
import Loader from '@/components/loader'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const {login, ssoLogin} = useUser()

  // Set by UserProvider when a session expires mid-use (see apiFetch).
  const sessionExpired = searchParams.get('reason') === 'session_expired'

    // Handle SSO redirect: SSO provider redirects to /login/?redirect=.../sso/&authenticated=true
    useEffect(() => {
        const redirect = searchParams.get('redirect')
        const authenticated = searchParams.get('authenticated')
        const deviceCode = searchParams.get('deviceCode')

        if (redirect && authenticated === 'true') {
            // Forward to the SSO callback page
            const redirectUrl = new URL(redirect)
            if (deviceCode) redirectUrl.searchParams.set('deviceCode', deviceCode)
            redirectUrl.searchParams.set('authenticated', 'true')
            window.location.href = redirectUrl.toString()
            return
        }

        if (deviceCode) {
            // Direct deviceCode on login page — forward to /sso/
            navigate(`/sso?deviceCode=${deviceCode}`)
            return
        }

        // MCP OAuth flow: persist the AS /authorize URL so we can return to it
        // (after password or SSO login) and complete the authorization.
        setReturnUrl(searchParams.get('returnUrl'))
    }, [searchParams])

    // Send the user back to the MCP OAuth /authorize URL if present, otherwise
    // to the default landing page.
    const finishLogin = () => {
        const returnUrl = popReturnUrl()
        if (returnUrl) {
            completeOAuthReturn(returnUrl)
        } else {
            navigate('/show/assets')
        }
    }

    const handleSubmit = async () => {
        setIsLoading(true)
        setError('')
        
        const response = await login(email, password)
        if (!response.ok) {
            setError(response.data.message || 'Log in failed')
            setIsLoading(false)
        }else{
            finishLogin()
        }
    }

    const handleSsoSubmit = async () => {
        setIsLoading(true)
        setError('')
        
        const response = await ssoLogin(email)
        if (!response.ok) {
            setError(response.data.message || 'Log in failed')
            setIsLoading(false)
        }
    }

  return (
    <div className="min-h-screen flex items-center justify-center">
        <div className={`max-w-md w-full space-y-8 p-8 bg-card rounded-lg border ${isLoading ? 'pointer-events-none opacity-60':''}`}>
            <div>
                <h2 className="text-center text-3xl font-bold text-secondary-foreground">
                    Sign in to your account
                </h2>
            </div>

            {sessionExpired && !error && (
                <div className="text-center text-sm rounded-md border border-yellow-500/40 bg-yellow-500/10 text-yellow-600 px-4 py-2">
                    Session Expired. Please login again
                </div>
            )}

            {error && (
                <div className={`text-center ${error.includes('403') ? 'text-yellow-500' : 'text-red-500'}`}>{error}</div>
            )}
            
            <Tabs defaultValue="sso" onValueChange={() => setError('')}>
                <TabsList className="w-full">
                    <TabsTrigger value="sso">Login with SSO</TabsTrigger>
                    <TabsTrigger value="password">Login with Password</TabsTrigger>
                </TabsList>

                <TabsContent value="sso" className="space-y-6 mt-6">
                    <div>
                        <label htmlFor="email-sso" className="text-secondary-foreground text-xs">
                            Email address
                        </label>
                        <Input
                            id="email-sso"
                            name="email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>
                    <Button
                        onClick={handleSsoSubmit}
                        className='w-full'
                    >
                        Continue with SSO
                    </Button>
                </TabsContent>

                <TabsContent value="password" className="space-y-6 mt-6">
                    <div>
                        <label htmlFor="email-password" className="text-secondary-foreground text-xs">
                            Email address
                        </label>
                        <Input
                            id="email-password"
                            name="email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>
                    <div>
                        <label htmlFor="password" className="text-secondary-foreground text-xs">
                            Password
                        </label>
                        <Input
                            id="password"
                            name="password"
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                        />
                    </div>
                    <Button
                        onClick={handleSubmit}
                        className='w-full'
                    >
                        Sign in
                    </Button>
                </TabsContent>
            </Tabs>
        </div>
        {isLoading && <Loader />}
    </div>
  )
}